import { toast } from "sonner";
import { Download, FileText } from "lucide-react";
import { useCalculatorStore } from "@/store/calculatorStore";
import { CONVERT } from "@/utils/ballisticsMath";
import { formatHoldover, HOLDOVER_UNIT_LABEL } from "@/utils/units";
import { useT } from "@/i18n/translations";
import { exportCSV, exportPDF } from "@/utils/exportCard";
import TrajectoryChart from "@/components/calculator/TrajectoryChart";
import ReticleView from "@/components/calculator/ReticleView";
import MpbrPanel from "@/components/calculator/MpbrPanel";
import pelletsData from "@/data/pellets.json";

const pelletName = (id) => {
  for (const cal of pelletsData.calibers) {
    const p = cal.pellets.find((x) => x.id === id);
    if (p) return p.name;
  }
  return "Custom";
};

const RangeTable = ({ samples, holdoverUnit, unitSystem, clickMrad, targetDistanceM }) => {
  const t = useT();
  const store = useCalculatorStore();
  const imperial = unitSystem === "imperial";
  const useMoa = holdoverUnit === "moa";
  const rows = samples.filter((s) => s.distanceM > 0);
  const distU = imperial ? "yd" : "m";

  const buildRow = (s) => {
    const dist = imperial ? CONVERT.mToYd(s.distanceM) : s.distanceM;
    const drop = formatHoldover(s.dropM, s.dropMrad, s.dropMoa, holdoverUnit);
    const corr = useMoa ? -s.dropMoa : -s.dropMrad;
    const clicks = clickMrad ? Math.round(Math.abs(s.dropMrad) / clickMrad) : 0;
    const wind = imperial ? CONVERT.mToIn(s.driftM) : s.driftM * 100;
    const vel = imperial ? CONVERT.msToFps(s.velocityMs) : s.velocityMs;
    const energy = imperial ? CONVERT.jToFtlb(s.energyJ) : s.energyJ;
    return {
      isTarget: Math.abs(s.distanceM - targetDistanceM) < 1e-6,
      cells: [
        dist.toFixed(0),
        drop.toFixed(2),
        `${corr >= 0 ? "+" : ""}${corr.toFixed(2)}`,
        String(clicks),
        wind.toFixed(1),
        vel.toFixed(0),
        energy.toFixed(1),
        (s.timeS * 1000).toFixed(0),
      ],
    };
  };

  const headers = [
    `${t("table.distance")} (${distU})`,
    `${t("table.drop")} (${HOLDOVER_UNIT_LABEL[holdoverUnit]})`,
    `${t("table.correction")} (${useMoa ? "MOA" : "MRAD"})`,
    t("table.clicks"),
    `${t("table.wind")} (${imperial ? "in" : "cm"})`,
    `${t("table.velocity")} (${imperial ? "fps" : "m/s"})`,
    `${t("table.energy")} (${imperial ? "ft-lbs" : "J"})`,
    `${t("table.time")} (ms)`,
  ];

  const buildExport = () => {
    const hour = Math.round(store.windDir / 30) % 12;
    const meta = [
      { label: t("export.pellet"), value: pelletName(store.selectedPelletId) },
      {
        label: t("export.v0"),
        value: imperial
          ? `${CONVERT.msToFps(store.v0).toFixed(0)} fps`
          : `${store.v0.toFixed(0)} m/s`,
      },
      { label: t("export.bc"), value: store.bc.toFixed(3) },
      {
        label: t("export.zero"),
        value: imperial
          ? `${CONVERT.mToYd(store.zeroDistance).toFixed(0)} yd`
          : `${store.zeroDistance.toFixed(0)} m`,
      },
      {
        label: t("export.target"),
        value: imperial
          ? `${CONVERT.mToYd(store.targetDistance).toFixed(0)} yd`
          : `${store.targetDistance.toFixed(0)} m`,
      },
      {
        label: t("export.wind"),
        value: imperial
          ? `${CONVERT.msToMph(store.windSpeed).toFixed(0)} mph @ ${hour === 0 ? 12 : hour}h`
          : `${store.windSpeed.toFixed(1)} m/s @ ${hour === 0 ? 12 : hour}h`,
      },
      {
        label: t("export.temp"),
        value: imperial ? `${CONVERT.cToF(store.tempC).toFixed(0)}°F` : `${store.tempC.toFixed(0)}°C`,
      },
      { label: t("export.location"), value: store.location || "–" },
      { label: t("export.generated"), value: new Date().toLocaleString() },
    ];
    const bodyRows = rows.map((s) => buildRow(s).cells);
    return { headers, rows: bodyRows, meta };
  };

  const onCSV = () => {
    const { headers: h, rows: r, meta } = buildExport();
    exportCSV({ headers: h, rows: r, meta, filename: "dase-ballistics-card" });
    toast.success(`${t("export.done")} (CSV)`);
  };
  const onPDF = () => {
    const { headers: h, rows: r, meta } = buildExport();
    exportPDF({ title: t("table.title"), headers: h, rows: r, meta, filename: "dase-ballistics-card" });
    toast.success(`${t("export.done")} (PDF)`);
  };

  return (
    <div className="overflow-hidden rounded-md border border-dase-border bg-dase-panel" data-testid="range-table">
      <div className="flex items-center justify-between border-b border-dase-border px-4 py-3">
        <h2 className="text-xs font-bold uppercase tracking-widest text-zinc-200">{t("table.title")}</h2>
        <div className="flex items-center gap-2">
          <button
            onClick={onCSV}
            data-testid="export-csv-btn"
            className="flex items-center gap-1 rounded border border-dase-border px-2 py-1 text-[10px] font-bold uppercase tracking-wide text-zinc-300 transition-colors hover:border-dase-red hover:text-dase-red"
          >
            <Download className="h-3 w-3" /> {t("export.csv")}
          </button>
          <button
            onClick={onPDF}
            data-testid="export-pdf-btn"
            className="flex items-center gap-1 rounded bg-dase-red px-2 py-1 text-[10px] font-bold uppercase tracking-wide text-white transition-colors hover:bg-dase-red-bright"
          >
            <FileText className="h-3 w-3" /> {t("export.pdf")}
          </button>
        </div>
      </div>
      <div className="max-h-[360px] overflow-auto">
        <table className="w-full font-mono text-[11px]">
          <thead className="sticky top-0 bg-dase-panel2 text-zinc-400">
            <tr className="text-left uppercase tracking-wider">
              {headers.map((h) => (
                <th key={h} className="px-3 py-2 font-semibold">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody className="text-zinc-300">
            {rows.map((s) => {
              const { isTarget, cells } = buildRow(s);
              return (
                <tr
                  key={s.distanceM}
                  data-testid={`range-row-${Math.round(s.distanceM)}`}
                  className={`border-t border-dase-border/60 ${
                    isTarget ? "bg-dase-red/10 text-zinc-100" : "hover:bg-white/[0.02]"
                  }`}
                >
                  <td className="px-3 py-1.5 font-bold">{cells[0]}</td>
                  <td className="px-3 py-1.5">{cells[1]}</td>
                  <td className="px-3 py-1.5 text-dase-red">{cells[2]}</td>
                  <td className="px-3 py-1.5">{cells[3]}</td>
                  <td className="px-3 py-1.5">{cells[4]}</td>
                  <td className="px-3 py-1.5">{cells[5]}</td>
                  <td className="px-3 py-1.5">{cells[6]}</td>
                  <td className="px-3 py-1.5">{cells[7]}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export const TrajectoryView = ({ result }) => {
  const holdoverUnit = useCalculatorStore((s) => s.holdoverUnit);
  const unitSystem = useCalculatorStore((s) => s.unitSystem);
  const clickMrad = useCalculatorStore((s) => s.clickMrad);
  const zeroDistance = useCalculatorStore((s) => s.zeroDistance);
  const { samples, metrics } = result;

  return (
    <div className="space-y-4" data-testid="trajectory-view">
      <TrajectoryChart
        samples={samples}
        holdoverUnit={holdoverUnit}
        unitSystem={unitSystem}
        targetDistanceM={metrics.targetDistanceM}
        zeroDistanceM={zeroDistance}
        clickMrad={clickMrad}
      />
      <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
        <ReticleView metrics={metrics} clickMrad={clickMrad} holdoverUnit={holdoverUnit} />
        <MpbrPanel />
      </div>
      <RangeTable
        samples={samples}
        holdoverUnit={holdoverUnit}
        unitSystem={unitSystem}
        clickMrad={clickMrad}
        targetDistanceM={metrics.targetDistanceM}
      />
    </div>
  );
};

export default TrajectoryView;
